import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Bell, CheckCircle } from '@phosphor-icons/react';

const CATEGORY_OF = (n) => {
  const l = n.link || '';
  if (l.startsWith('/fees') || /fee|invoice|receipt|payment|payslip|salary/i.test(n.title)) return 'Finance';
  if (l.startsWith('/cctv') || l.startsWith('/bgv') || /camera|bgv|security|break-glass/i.test(n.title)) return 'Security';
  if (l.startsWith('/transport') || /bus/i.test(n.title)) return 'Transport';
  if (l.startsWith('/library') || /book/i.test(n.title)) return 'Library';
  if (l.startsWith('/hr') || /leave/i.test(n.title)) return 'HR';
  if (l.startsWith('/chat') || /message|meeting/i.test(n.title)) return 'Messages';
  if (/attendance|exam|report card|admission|grade/i.test(n.title)) return 'Academic';
  return 'System';
};
const CAT_STYLE = { Finance: 'bg-green-100 text-green-800', Security: 'bg-rose-100 text-rose-700', Transport: 'bg-amber-100 text-amber-800', Library: 'bg-blue-100 text-blue-700', HR: 'bg-purple-100 text-purple-700', Messages: 'bg-sky-100 text-sky-700', Academic: 'bg-orange-100 text-orange-700', System: 'bg-slate-100 text-slate-600' };

export default function Notifications() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { data: notifs } = useQuery({ queryKey: ['notifs-all'], queryFn: () => api.get('/notifications', { params: { limit: 200 } }).then((r) => r.data) });
  const [cat, setCat] = useState('all');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const readAllMut = useMutation({
    mutationFn: () => api.post('/notifications/read-all'),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['notifs-all'] }); qc.invalidateQueries({ queryKey: ['notifs'] }); },
  });
  const openMut = useMutation({ mutationFn: (id) => api.post(`/notifications/${id}/read`) });

  const enriched = (notifs || []).map((n) => ({ ...n, category: CATEGORY_OF(n) }));
  const cats = [...new Set(enriched.map((n) => n.category))];
  const filtered = enriched.filter((n) => (cat === 'all' || n.category === cat) && (!unreadOnly || !n.read));
  const unread = enriched.filter((n) => !n.read).length;
  const open = (n) => {
    if (!n.read) { openMut.mutate(n.id); qc.setQueryData(['notifs-all'], (old) => (old || []).map((x) => (x.id === n.id ? { ...x, read: true } : x))); }
    if (n.link) nav(n.link);
  };

  return (
    <div data-testid="notifications-page">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-1">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Notification Center</h1>
        {unread > 0 && (
          <button data-testid="notif-read-all" onClick={() => readAllMut.mutate()} className="text-xs border border-slate-200 rounded px-3 py-1.5 hover:border-slate-900 transition-colors flex items-center gap-1.5">
            <CheckCircle size={14} /> Mark all {unread} read
          </button>
        )}
      </div>
      <p className="text-sm text-slate-500 mb-6">Every alert, categorised - tap one to jump straight to the action</p>

      <div className="flex flex-wrap gap-2 mb-4">
        <button data-testid="notif-filter-all" onClick={() => setCat('all')} className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${cat === 'all' ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-200 hover:border-slate-900'}`}>All ({enriched.length})</button>
        {cats.map((c) => (
          <button key={c} data-testid={`notif-filter-${c.toLowerCase()}`} onClick={() => setCat(c)} className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${cat === c ? 'bg-orange-600 text-white border-orange-600' : 'border-slate-200 hover:border-orange-500'}`}>{c} ({enriched.filter((n) => n.category === c).length})</button>
        ))}
        <button data-testid="notif-filter-unread" onClick={() => setUnreadOnly(!unreadOnly)} className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${unreadOnly ? 'bg-rose-600 text-white border-rose-600' : 'border-slate-200 hover:border-rose-500'}`}>Unread ({unread})</button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md divide-y divide-slate-50" data-testid="notif-list">
        {filtered.map((n) => (
          <button key={n.id} onClick={() => open(n)} data-testid={`notif-item-${n.id}`}
            className={`w-full text-left p-3.5 hover:bg-orange-50/40 transition-colors ${!n.read ? 'bg-blue-50/40' : ''}`}>
            <div className="flex items-center gap-2 flex-wrap">
              {!n.read && <span className="w-2 h-2 rounded-full bg-orange-500 shrink-0" />}
              <span className="font-medium text-sm">{n.title}</span>
              <span className={`font-mono-label text-[9px] uppercase px-1.5 py-0.5 rounded ${CAT_STYLE[n.category]}`}>{n.category}</span>
              <span className="ms-auto text-[10px] text-slate-400">{(n.created_at || '').slice(0, 16).replace('T', ' ')}</span>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">{n.body}</p>
            {n.link && <span className="text-[10px] text-orange-700 underline">open {n.link}</span>}
          </button>
        ))}
        {filtered.length === 0 && (
          <div className="p-10 text-center text-slate-400">
            <Bell size={28} className="mx-auto mb-2" />
            <p className="text-sm">Nothing here - you're all caught up.</p>
          </div>
        )}
      </div>
    </div>
  );
}
